/**
 * Audio engine (GDD §14.3, issue #75): Web Audio with a music bus and an effects bus under a master gain.
 * It never throws and never logs an error: with no AudioContext, a constructor that throws or a `resume()`
 * that is refused, the state is `unavailable` and every operation is a no-op (GDD §6.4: the game works
 * without audio). Sounds themselves arrive with #76 (effects) and #77 (music).
 *
 *   music -> musicGain -+
 *                       +-> master -> destination
 *   sfx   -> sfxGain  --+
 */
export type AudioState = 'unavailable' | 'suspended' | 'running';

/** What the engine needs from the browser's AudioContext, so a fake one drives the tests. */
export interface AudioContextLike {
  readonly state: string;
  readonly currentTime: number;
  readonly destination: unknown;
  createGain(): GainNodeLike;
  createBuffer(channels: number, length: number, sampleRate: number): AudioBufferLike;
  createBufferSource(): BufferSourceLike;
  readonly sampleRate: number;
  resume(): Promise<void>;
  suspend(): Promise<void>;
}

export interface AudioBufferLike {
  readonly duration: number;
  getChannelData(channel: number): Float32Array;
}

export interface BufferSourceLike {
  buffer: AudioBufferLike | null;
  connect(destination: unknown): unknown;
  start(when?: number): void;
}

export interface GainNodeLike {
  readonly gain: { value: number; setTargetAtTime(target: number, startTime: number, timeConstant: number): unknown };
  connect(destination: unknown): unknown;
}

export interface AudioVolumes {
  musicVolume: number;
  sfxVolume: number;
}

export interface AudioEngineOptions {
  /** Creates the context; may throw or return null when there is no Web Audio. */
  createContext: () => AudioContextLike | null;
  volumes: AudioVolumes;
}

/** Why the audio was suspended: a pause holds it until RESUME; a hidden page is lifted by an interaction. */
export type SuspendReason = 'pause' | 'hidden';

export interface AudioEngine {
  readonly state: AudioState;
  readonly musicGain: number;
  readonly sfxGain: number;
  /** The context sounds are created in (#76, #77); null while unavailable. */
  readonly context: AudioContextLike | null;
  /** The nodes sounds connect to (#76, #77); null while unavailable. */
  readonly buses: { music: GainNodeLike; sfx: GainNodeLike } | null;
  /** First user interaction: starts the context. Does nothing while a pause holds the audio. */
  unlock(): Promise<void>;
  applyVolumes(volumes: AudioVolumes): void;
  suspend(reason: SuspendReason): Promise<void>;
  /** The player resumed the game explicitly. */
  resume(): Promise<void>;
}

/** Short time constant so a volume change has no click (seconds). */
const GAIN_TIME_CONSTANT = 0.015;

const clamp01 = (value: number) => Math.min(1, Math.max(0, Number.isFinite(value) ? value : 0));

export function createAudioEngine({ createContext, volumes }: AudioEngineOptions): AudioEngine {
  let context: AudioContextLike | null = null;
  let failed = false;
  let held = false;
  let music: GainNodeLike | null = null;
  let sfx: GainNodeLike | null = null;
  let wanted = { music: clamp01(volumes.musicVolume), sfx: clamp01(volumes.sfxVolume) };

  try {
    context = createContext();
    if (context) {
      const master = context.createGain();
      music = context.createGain();
      sfx = context.createGain();
      music.connect(master);
      sfx.connect(master);
      master.connect(context.destination);
      music.gain.value = wanted.music;
      sfx.gain.value = wanted.sfx;
    }
  } catch {
    context = null;
    music = null;
    sfx = null;
  }

  const setGain = (node: GainNodeLike | null, value: number) => {
    if (!node || !context) return;
    // A suspended context does not advance its clock, so the value is set directly.
    if (context.state === 'running') node.gain.setTargetAtTime(value, context.currentTime, GAIN_TIME_CONSTANT);
    else node.gain.value = value;
  };

  const attempt = async (action: () => Promise<void>) => {
    try {
      await action();
    } catch {
      failed = true;
    }
  };

  return {
    get state(): AudioState {
      if (!context || failed) return 'unavailable';
      return context.state === 'running' ? 'running' : 'suspended';
    },
    // The volume the buses are heading for (a change ramps over a few milliseconds).
    get musicGain() {
      return music ? wanted.music : 0;
    },
    get sfxGain() {
      return sfx ? wanted.sfx : 0;
    },
    get context() {
      return failed ? null : context;
    },
    get buses() {
      return context && music && sfx && !failed ? { music, sfx } : null;
    },
    async unlock() {
      if (!context || failed || held) return;
      await attempt(() => context!.resume());
    },
    applyVolumes(next) {
      wanted = { music: clamp01(next.musicVolume), sfx: clamp01(next.sfxVolume) };
      setGain(music, wanted.music);
      setGain(sfx, wanted.sfx);
    },
    async suspend(reason) {
      if (!context || failed) return;
      if (reason === 'pause') held = true;
      await attempt(() => context!.suspend());
    },
    async resume() {
      held = false;
      if (!context || failed) return;
      await attempt(() => context!.resume());
    },
  };
}

/** The browser's Web Audio, or null when it is missing (including the prefixed constructor of old Safari). */
export function browserAudioContext(): AudioContextLike | null {
  const scope = globalThis as unknown as {
    AudioContext?: new () => AudioContextLike;
    webkitAudioContext?: new () => AudioContextLike;
  };
  const Constructor = scope.AudioContext ?? scope.webkitAudioContext;
  return Constructor ? new Constructor() : null;
}
